import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "@/lib/apiClient";
import { LogOut, Search, PackageCheck, RefreshCw, PlusCircle, FileText, Download } from "lucide-react";

const TKEY = "rentopc.rental.token";
const getTok = () => localStorage.getItem(TKEY);
const rapi = axios.create({ baseURL: `${API_BASE}/rental` });
rapi.interceptors.request.use((c) => { const t = getTok(); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const KAPIL = "IMPORTANT: Please also email a copy of this request to Kapil@rentopc.com for processing.";
const TYPES = ["laptop", "monitor", "keyboard", "mouse", "charger"];

function Notice() {
  return <div data-testid="kapil-notice" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">{KAPIL}</div>;
}

function Login({ onOk }) {
  const [u, setU] = useState(""); const [p, setP] = useState(""); const [e, setE] = useState(""); const [b, setB] = useState(false);
  const submit = async (ev) => {
    ev.preventDefault(); setB(true); setE("");
    try {
      const { data } = await rapi.post("/portal/login", { username: u, password: p });
      localStorage.setItem(TKEY, data.access_token); onOk();
    } catch { setE("Incorrect username or password"); } finally { setB(false); }
  };
  return (
    <div className="min-h-screen bg-[#0b1220] flex items-center justify-center px-4" data-testid="rental-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-2xl space-y-3">
        <div className="text-lg font-extrabold">RentOPC — Rental Portal</div>
        <input value={u} onChange={(e) => setU(e.target.value)} placeholder="Username" data-testid="rental-login-user" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <input type="password" value={p} onChange={(e) => setP(e.target.value)} placeholder="Password" data-testid="rental-login-pass" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        {e && <div className="text-sm text-rose-600">{e}</div>}
        <button disabled={b} data-testid="rental-login-submit" className="w-full rounded-lg bg-slate-900 text-white py-2 text-sm font-semibold disabled:opacity-50">{b ? "…" : "Sign in"}</button>
      </form>
    </div>
  );
}

const assetNum = (s) => { const m = String(s || "").match(/(\d+)\s*$/); return m ? parseInt(m[1], 10) : 0; };
const sortDesc = (arr) => [...arr].sort((a, b) => assetNum(b.asset_id) - assetNum(a.asset_id) || String(b.asset_id).localeCompare(String(a.asset_id)));


export default function RentalPortalPage() {
  const [authed, setAuthed] = useState(!!getTok());
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("assets");
  const [assets, setAssets] = useState([]);
  const [requests, setRequests] = useState([]);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    rapi.get("/portal/me").then((r) => setMe(r.data)).catch(() => { localStorage.removeItem(TKEY); setAuthed(false); });
    rapi.get("/portal/assets").then((r) => setAssets(r.data)).catch(() => {});
    rapi.get("/portal/requests").then((r) => setRequests(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (authed) load(); }, [authed, load]);

  if (!authed) return <Login onOk={() => setAuthed(true)} />;

  const logout = () => { localStorage.removeItem(TKEY); setAuthed(false); setMe(null); };
  const receive = async (id) => { await rapi.post(`/portal/assets/${id}/receive`); load(); };

  return (
    <div className="min-h-screen bg-[#f5f7fa]" data-testid="rental-portal">
      <header className="bg-[#0b1220] text-white">
        <div className="mx-auto max-w-5xl px-4 py-4 flex items-center justify-between">
          <div><div className="font-extrabold">RentOPC Rental Portal</div><div className="text-xs text-slate-400">{me?.name}</div></div>
          <button onClick={logout} data-testid="rental-logout" className="inline-flex items-center gap-1.5 text-sm border border-white/20 rounded-lg px-3 py-1.5"><LogOut size={14} /> Sign out</button>
        </div>
      </header>
      <div className="mx-auto max-w-5xl px-4 py-5">
        <div className="flex flex-wrap gap-2 mb-4">
          {[["assets", "My Assets", PackageCheck], ["replacement", "Replacement", RefreshCw], ["new", "New Asset", PlusCircle], ["report", "Report", FileText], ["requests", "My Requests", FileText]].map(([k, l, Ic]) => (
            <button key={k} onClick={() => { setTab(k); setMsg(""); }} data-testid={`tab-${k}`}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm ${tab === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200"}`}><Ic size={14} /> {l}</button>
          ))}
        </div>
        {msg && <div data-testid="rental-success" className="mb-3 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{msg}</div>}

        {tab === "assets" && <AssetList assets={assets} onReceive={receive} />}
        {tab === "replacement" && <ReplacementTab onDone={(m) => { setMsg(m); load(); }} />}
        {tab === "new" && <NewAssetTab onDone={(m) => { setMsg(m); load(); }} />}
        {tab === "report" && <ReportTab onDone={(m) => { setMsg(m); load(); }} />}
        {tab === "requests" && <RequestsTab requests={requests} />}
      </div>
    </div>
  );
}

function AssetList({ assets, onReceive }) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const sorted = sortDesc(assets);
  const filtered = query
    ? sorted.filter((a) => [a.asset_id, a.serial_number, a.brand, a.model].some((v) => v && String(v).toLowerCase().includes(query)))
    : sorted;
  return (
    <div className="space-y-3" data-testid="asset-list">
      <div className="relative max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by Asset ID, serial, brand/model" data-testid="assets-search" className="w-full rounded-lg border border-slate-200 pl-9 pr-16 py-2 text-sm" />
        {q && <button onClick={() => setQ("")} data-testid="assets-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500 underline">Clear</button>}
      </div>
      {!assets.length ? (
        <div className="text-slate-500 text-sm">No assets allocated yet.</div>
      ) : !filtered.length ? (
        <div data-testid="assets-empty" className="text-slate-500 text-sm">No matching asset in your account.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm whitespace-nowrap" data-testid="assets-table">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr>
              <th className="text-left px-3 py-2">Asset ID</th><th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Brand / Model</th><th className="text-left px-3 py-2">Processor</th>
              <th className="text-left px-3 py-2">RAM</th><th className="text-left px-3 py-2">Storage</th>
              <th className="text-left px-3 py-2">Serial</th><th className="text-left px-3 py-2">Status</th><th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>{filtered.map((a) => (
              <tr key={a.asset_id} className="border-t border-slate-100" data-testid={`asset-${a.asset_id}`}>
                <td className="px-3 py-2 font-mono font-semibold">{a.asset_id}</td>
                <td className="px-3 py-2 capitalize">{a.asset_type}</td>
                <td className="px-3 py-2">{[a.brand, a.model].filter(Boolean).join(" ") || "—"}</td>
                <td className="px-3 py-2">{a.processor || "—"}</td>
                <td className="px-3 py-2">{a.ram || "—"}</td>
                <td className="px-3 py-2">{a.storage || "—"}</td>
                <td className="px-3 py-2">{a.serial_number || "—"}</td>
                <td className="px-3 py-2"><span className="text-[11px] font-bold rounded-full bg-slate-100 px-2 py-0.5">{a.status}</span></td>
                <td className="px-3 py-2 text-right">{a.status === "SHIPPED" && onReceive && <button onClick={() => onReceive(a.id)} data-testid={`receive-${a.asset_id}`} className="rounded-lg bg-emerald-600 text-white px-3 py-1 text-xs">Mark Received</button>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReplacementTab({ onDone }) {
  const [id, setId] = useState(""); const [ser, setSer] = useState(""); const [issue, setIssue] = useState(""); const [b, setB] = useState(false); const [e, setE] = useState("");
  const submit = async () => {
    setB(true); setE("");
    try { const { data } = await rapi.post("/portal/replacement", { asset_id: id || undefined, serial: ser || undefined, issue_description: issue }); onDone(`Replacement request ${data.request_no} submitted. ${data.message}`); }
    catch (err) { setE(err?.response?.data?.detail?.message || "Failed"); } finally { setB(false); }
  };
  return (
    <div className="space-y-3 max-w-lg" data-testid="replacement-form">
      <Notice />
      <input value={id} onChange={(e) => setId(e.target.value)} placeholder="Asset ID" data-testid="repl-asset-id" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
      <input value={ser} onChange={(e) => setSer(e.target.value)} placeholder="or Serial number" data-testid="repl-serial" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
      <textarea value={issue} onChange={(e) => setIssue(e.target.value)} placeholder="Problem / issue description (required)" rows={3} data-testid="repl-issue" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
      {e && <div className="text-sm text-rose-600">{e}</div>}
      <button disabled={b || !issue.trim() || (!id && !ser)} onClick={submit} data-testid="repl-submit" className="rounded-lg bg-slate-900 text-white px-4 py-2 text-sm disabled:opacity-40">Request Replacement</button>
    </div>
  );
}

function NewAssetTab({ onDone }) {
  const [t, setT] = useState("laptop"); const [qty, setQty] = useState(1); const [rem, setRem] = useState(""); const [b, setB] = useState(false);
  const submit = async () => { setB(true); try { const { data } = await rapi.post("/portal/new-asset", { asset_type: t, quantity: Number(qty), remark: rem || undefined }); onDone(`New asset request ${data.request_no} submitted. ${data.message}`); } finally { setB(false); } };
  return (
    <div className="space-y-3 max-w-lg" data-testid="new-asset-form">
      <Notice />
      <select value={t} onChange={(e) => setT(e.target.value)} data-testid="new-type" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm capitalize">{TYPES.map((x) => <option key={x} value={x}>{x}</option>)}</select>
      <input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="new-qty" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
      <input value={rem} onChange={(e) => setRem(e.target.value)} placeholder="Short remark (optional)" data-testid="new-remark" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
      <button disabled={b} onClick={submit} data-testid="new-submit" className="rounded-lg bg-slate-900 text-white px-4 py-2 text-sm disabled:opacity-50">Submit Request</button>
    </div>
  );
}

function ReportTab({ onDone }) {
  const [rt, setRt] = useState("Asset summary"); const [rem, setRem] = useState(""); const [b, setB] = useState(false); const [dl, setDl] = useState("");
  const submit = async () => { setB(true); try { const { data } = await rapi.post("/portal/report-request", { report_type: rt, remark: rem || undefined }); onDone(`Report request ${data.request_no} submitted. ${data.message}`); } finally { setB(false); } };
  const download = async (fmt) => {
    setDl(fmt);
    try {
      const res = await rapi.get("/portal/report", { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `my_rental_report.${fmt === "xlsx" ? "xlsx" : "csv"}`; a.click(); URL.revokeObjectURL(url);
    } finally { setDl(""); }
  };
  return (
    <div className="space-y-5 max-w-lg">
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="report-download">
        <div className="text-sm font-semibold text-slate-900">Download Complete Report</div>
        <p className="text-xs text-slate-500">A full report of all your allocated assets and their details.</p>
        <div className="flex gap-2">
          <button onClick={() => download("xlsx")} disabled={!!dl} data-testid="report-download-xlsx" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 text-white px-4 py-2 text-sm disabled:opacity-50"><Download size={15} /> {dl === "xlsx" ? "Preparing…" : "Download Excel"}</button>
          <button onClick={() => download("csv")} disabled={!!dl} data-testid="report-download-csv" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm disabled:opacity-50"><Download size={15} /> {dl === "csv" ? "Preparing…" : "Download CSV"}</button>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="report-form">
        <div className="text-sm font-semibold text-slate-900">Request a Report</div>
        <Notice />
        <input value={rt} onChange={(e) => setRt(e.target.value)} placeholder="Report type" data-testid="report-type" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <input value={rem} onChange={(e) => setRem(e.target.value)} placeholder="Optional remark" data-testid="report-remark" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <button disabled={b} onClick={submit} data-testid="report-submit" className="rounded-lg bg-slate-900 text-white px-4 py-2 text-sm disabled:opacity-50">Request Report</button>
      </div>
    </div>
  );
}

function RequestsTab({ requests }) {
  if (!requests.length) return <div className="text-slate-500 text-sm">No requests yet.</div>;
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="requests-table">
      <table className="w-full text-sm"><thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left px-3 py-2">Request</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Asset/Qty</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Date</th></tr></thead>
      <tbody>{requests.map((r) => <tr key={r.request_no} className="border-t border-slate-100"><td className="px-3 py-2 font-mono">{r.request_no}</td><td className="px-3 py-2">{r.request_type}</td><td className="px-3 py-2">{r.asset_code || (r.quantity ? `${r.asset_type} × ${r.quantity}` : r.report_type)}</td><td className="px-3 py-2">{r.status}</td><td className="px-3 py-2">{new Date(r.created_at).toLocaleDateString("en-IN")}</td></tr>)}</tbody></table>
    </div>
  );
}
